/**
 * The company's template Geosoft folder kept in Teslyn Cloud and copied to every Teslyn Field laptop (MB's decision
 * 889; docs/specs/cloud/geosoft-templates.md). One set per aircraft type (decision 506: the register's type picks
 * it): every file of the type's `Scripts\<folder>` (`QC_SCRIPT_FOLDERS`, `.bak` left out), its `Views\<folder>`
 * (`QC_VIEW_FOLDERS`), every other view its scripts load (`SETINI LOADDBVU.FILE`, relative to the Geosoft folder), and
 * the `IO_Templates` (MB's R146 and R148). `copy_template_for_block` in Teslyn Field copies the same files.
 *
 * Teslyn Cloud's Aircraft page picks a Geosoft folder ({@link geosoftTemplateFind}, then {@link geosoftTemplatePlan}
 * once the scripts are read), stores each file by its SHA-256 ({@link geosoftTemplateObjectPath}) and replaces the
 * type's manifest (`geosoft_template_replace`). The manifest syncs to the laptops, which download the files into
 * their own copy and use it when a project lacks the type's scripts.
 */
import type { Uuid } from './model.js';
import type { QcFamily } from './geosoft-qc.js';
import { AIRFRAME_LABEL } from './entry-card.js';
import { QC_SCRIPT_FOLDERS, QC_VIEW_FOLDERS } from './qc-review-view.js';

/** The private bucket of the template files. */
export const GEOSOFT_TEMPLATE_BUCKET = 'geosoft-templates';
/** The largest template file (the bucket's limit too). */
export const GEOSOFT_TEMPLATE_MAX_FILE_BYTES = 10 * 1024 * 1024;
/** The most files one aircraft type's set may hold. */
export const GEOSOFT_TEMPLATE_MAX_FILES = 400;
/** The longest relative path of a template file. */
export const GEOSOFT_TEMPLATE_MAX_PATH = 400;
/** The aircraft types with a set, in the order Teslyn shows them. */
export const GEOSOFT_TEMPLATE_FAMILIES: readonly QcFamily[] = ['c208', 'c210', 'heli'];
/** Who uploads and removes a set (decision 889's brief; the writers of decision 147). Every staff member reads. */
export const GEOSOFT_TEMPLATE_WRITER_ROLES = ['tenant_admin', 'manager', 'data_processor'] as const;

/** One file of a type's set: a row of `geosoft_template_file` (Teslyn Cloud's, synced down only). */
export interface GeosoftTemplateFile {
  readonly id: Uuid;
  readonly tenant_id: Uuid;
  readonly family: QcFamily;
  /** Relative to the Geosoft folder, `/` separated: `Scripts/C210_Scripts_V2.10/C210_Script_1_V2.10_No_ADU.gs`. */
  readonly rel_path: string;
  readonly size_bytes: number;
  readonly sha256: string;
  readonly object_path: string;
  readonly uploaded_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by: Uuid | null;
  readonly deleted_at: string | null;
}

/** True when any of `roles` may upload or remove a set. */
export function canEditGeosoftTemplates(roles: readonly string[]): boolean {
  return roles.some((r) => (GEOSOFT_TEMPLATE_WRITER_ROLES as readonly string[]).includes(r));
}

const SHA256 = /^[0-9a-f]{64}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The object of a template file: named by its content, so it is never overwritten and stored once. */
export function geosoftTemplateObjectPath(tenantId: Uuid, sha256: string): string {
  if (!UUID.test(tenantId)) throw new Error(`${tenantId} is not an organisation id.`);
  if (!SHA256.test(sha256)) throw new Error(`${sha256} is not a SHA-256.`);
  return `tenants/${tenantId}/geosoft-templates/${sha256}`;
}

/** The same check as the database's: plain names under `Scripts/`, `Views/` or `IO_Templates/`, `/` separated. */
export function geosoftTemplateRelPathOk(rel: string): boolean {
  if (rel.length === 0 || rel.length > GEOSOFT_TEMPLATE_MAX_PATH) return false;
  const parts = rel.split('/');
  if (parts.length < 2) return false;
  const top = parts[0]!.toLowerCase();
  if (top !== 'scripts' && top !== 'views' && top !== 'io_templates') return false;
  return parts.every(
    (p) =>
      p.length > 0 &&
      p !== '.' &&
      p !== '..' &&
      !/[\\:*?"<>|]/.test(p) &&
      ![...p].some((c) => c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f) &&
      p.trim() === p,
  );
}

/**
 * The views a script loads, as written (`SETINI LOADDBVU.FILE="…"`, Geosoft's doubled backslashes made single), in
 * order; comment lines (`/`) left out. The same reading as Teslyn Field's `review::script_views`.
 */
export function geosoftScriptViews(script: string): string[] {
  const out: string[] = [];
  for (const raw of script.split(/\r?\n/)) {
    const line = raw.trim();
    if (line.length === 0 || line.startsWith('/')) continue;
    if (!line.startsWith('SETINI')) continue;
    const rest = line.slice('SETINI'.length).trimStart();
    const eq = rest.indexOf('=');
    if (eq < 0) continue;
    if (rest.slice(0, eq).trim().toUpperCase() !== 'LOADDBVU.FILE') continue;
    const value = rest
      .slice(eq + 1)
      .trim()
      .replace(/^"+|"+$/g, '');
    out.push(value.replaceAll('\\\\', '\\'));
  }
  return out;
}

/**
 * The coordinate systems a script sets (`SETINI IPJ.NAME="\"WGS 84 / UTM zone 59S\""`, comments left out), as written.
 * The same reading as `teslyn_geosoft::template_projection::audit`.
 */
export function geosoftScriptZones(script: string): string[] {
  const out: string[] = [];
  for (const raw of script.split(/\r?\n/)) {
    const m = /^\s*SETINI\s+(IPJ\.NAME|IPJSET\.S1PCS)\s*=(.*)$/i.exec(raw);
    if (!m) continue;
    const name = m[2]!
      .replaceAll('\\"', '')
      .trim()
      .replace(/^"+|"+$/g, '');
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

/** A view as a script names it (`.\Views\x.dbview`) as a relative path (`Views/x.dbview`), or null when it is not one. */
export function geosoftViewRelPath(view: string): string | null {
  const parts = view.split(/[\\/]/).filter((p) => p.length > 0 && p !== '.');
  const rel = parts.join('/');
  return geosoftTemplateRelPathOk(rel) ? rel : null;
}

/** A file of the folder picked in the browser: its path below the pick (`webkitRelativePath`, `/` separated). */
export interface PickedFile {
  readonly path: string;
  readonly size: number;
}

export type GeosoftTemplateFind =
  | {
      readonly ok: true;
      /** The Geosoft folder within the pick (`Geosoft`, or '' when the pick is it). */
      readonly root: string;
      /** The type's script folder as named on disk. */
      readonly folder: string;
      /** The picked paths of the folder's scripts (`.gs`), to read for the views they load. */
      readonly scripts: readonly string[];
    }
  | { readonly ok: false; readonly message: string };

const isBak = (name: string): boolean => name.toLowerCase().endsWith('.bak');
const isScript = (name: string): boolean => name.toLowerCase().endsWith('.gs');
const parts = (p: string): string[] => p.split(/[\\/]/).filter((x) => x.length > 0);

/** The type's script folder in the picked folder: the Geosoft folder holding `Scripts\<folder>`, and its scripts. */
export function geosoftTemplateFind(family: QcFamily, picked: readonly PickedFile[]): GeosoftTemplateFind {
  const folder = QC_SCRIPT_FOLDERS[family];
  const want = folder.toLowerCase();
  const roots = new Map<string, string>();
  const scripts: string[] = [];
  let disk = folder;
  for (const f of picked) {
    const p = parts(f.path);
    for (let i = 0; i + 2 < p.length; i++) {
      if (p[i]!.toLowerCase() !== 'scripts' || p[i + 1]!.toLowerCase() !== want || i + 3 !== p.length)
        continue;
      const root = p.slice(0, i).join('/');
      roots.set(root.toLowerCase(), root);
      disk = p[i + 1]!;
      if (isScript(p[i + 2]!)) scripts.push(f.path);
    }
  }
  const label = AIRFRAME_LABEL[family];
  if (roots.size === 0)
    return {
      ok: false,
      message: `The folder has no Scripts\\${folder} folder for the ${label}. Choose the Geosoft folder that holds the aircraft types' Scripts and Views folders.`,
    };
  if (roots.size > 1)
    return {
      ok: false,
      message: `The folder holds ${roots.size} Geosoft folders with Scripts\\${folder} (${[...roots.values()]
        .map((r) => r || 'the folder itself')
        .join(', ')}). Choose one Geosoft folder.`,
    };
  const root = [...roots.values()][0]!;
  return { ok: true, root, folder: disk, scripts: scripts.filter((s) => !isBak(s)).sort() };
}

/** One file of a planned upload: where it is in the pick, its path in the set, its size. */
export interface GeosoftTemplatePlanFile {
  readonly path: string;
  readonly rel_path: string;
  readonly size: number;
}

export type GeosoftTemplatePlan =
  | {
      readonly ok: true;
      readonly files: readonly GeosoftTemplatePlanFile[];
      /** Views a script loads that are not in the folder (as the script names them): left out. */
      readonly missingViews: readonly string[];
      /** Scripts that set a coordinate system, and which (Teslyn Field sets each block's own when it copies them). */
      readonly scriptZones: readonly { readonly rel_path: string; readonly zones: readonly string[] }[];
    }
  | { readonly ok: false; readonly message: string };

/**
 * What a type's set is from the picked folder: every file of `Scripts\<folder>` and `Views\<its folder>` but `.bak`
 * files, every other view the scripts load (each once), and the IO templates. `scriptTexts` holds the text of each of
 * `found.scripts`.
 */
export function geosoftTemplatePlan(
  family: QcFamily,
  picked: readonly PickedFile[],
  found: Extract<GeosoftTemplateFind, { ok: true }>,
  scriptTexts: ReadonlyMap<string, string>,
): GeosoftTemplatePlan {
  const rootParts = parts(found.root);
  const byRel = new Map<string, PickedFile & { readonly rel: string }>();
  for (const f of picked) {
    const p = parts(f.path);
    if (p.slice(0, rootParts.length).join('/').toLowerCase() !== found.root.toLowerCase()) continue;
    const rel = p.slice(rootParts.length).join('/');
    if (rel) byRel.set(rel.toLowerCase(), { ...f, rel });
  }
  const files = new Map<string, GeosoftTemplatePlanFile>();
  const want = found.folder.toLowerCase();
  const add = (f: PickedFile & { readonly rel: string }): string | null => {
    if (!geosoftTemplateRelPathOk(f.rel)) return `${f.rel} cannot be kept in a template (its name).`;
    if (f.size > GEOSOFT_TEMPLATE_MAX_FILE_BYTES) return `${f.rel} is larger than 10 MB.`;
    files.set(f.rel.toLowerCase(), { path: f.path, rel_path: f.rel, size: f.size });
    return null;
  };
  for (const f of [...byRel.values()].sort((a, b) => a.rel.localeCompare(b.rel))) {
    const p = f.rel.split('/');
    if (p.length !== 3 || p[0]!.toLowerCase() !== 'scripts' || p[1]!.toLowerCase() !== want || isBak(p[2]!))
      continue;
    const problem = add(f);
    if (problem) return { ok: false, message: problem };
  }
  const viewFolder = QC_VIEW_FOLDERS[family].toLowerCase();
  for (const f of [...byRel.values()].sort((a, b) => a.rel.localeCompare(b.rel))) {
    const p = f.rel.split('/');
    const top = p[0]!.toLowerCase();
    const own = top === 'views' && p.length === 3 && p[1]!.toLowerCase() === viewFolder;
    const io = top === 'io_templates' && p.length === 2;
    if ((!own && !io) || isBak(p[p.length - 1]!)) continue;
    const problem = add(f);
    if (problem) return { ok: false, message: problem };
  }
  const missing: string[] = [];
  const scriptZones: { readonly rel_path: string; readonly zones: readonly string[] }[] = [];
  for (const script of found.scripts) {
    const zones = geosoftScriptZones(scriptTexts.get(script) ?? '');
    if (zones.length > 0) {
      const rel = parts(script).slice(rootParts.length).join('/');
      scriptZones.push({ rel_path: rel, zones });
    }
    for (const view of geosoftScriptViews(scriptTexts.get(script) ?? '')) {
      const rel = geosoftViewRelPath(view);
      const f = rel ? byRel.get(rel.toLowerCase()) : undefined;
      if (!f) {
        if (!missing.includes(view)) missing.push(view);
        continue;
      }
      if (files.has(f.rel.toLowerCase())) continue;
      const problem = add(f);
      if (problem) return { ok: false, message: problem };
    }
  }
  const list = [...files.values()].sort((a, b) => a.rel_path.localeCompare(b.rel_path));
  if (list.length === 0) return { ok: false, message: `Scripts\\${found.folder} holds no files to keep.` };
  if (list.length > GEOSOFT_TEMPLATE_MAX_FILES)
    return {
      ok: false,
      message: `The ${AIRFRAME_LABEL[family]}'s set would be ${list.length} files; a set holds at most ${GEOSOFT_TEMPLATE_MAX_FILES}.`,
    };
  return { ok: true, files: list, missingViews: missing, scriptZones };
}

/** What an upload changes in a type's set (paths compared whatever the case). */
export interface GeosoftTemplateDiff {
  readonly added: number;
  readonly changed: number;
  readonly removed: number;
  readonly unchanged: number;
}

export function geosoftTemplateDiff(
  current: readonly { readonly rel_path: string; readonly sha256: string }[],
  next: readonly { readonly rel_path: string; readonly sha256: string }[],
): GeosoftTemplateDiff {
  const now = new Map(current.map((f) => [f.rel_path.toLowerCase(), f.sha256]));
  const then = new Set(next.map((f) => f.rel_path.toLowerCase()));
  let added = 0;
  let changed = 0;
  let unchanged = 0;
  for (const f of next) {
    const was = now.get(f.rel_path.toLowerCase());
    if (was === undefined) added++;
    else if (was === f.sha256) unchanged++;
    else changed++;
  }
  const removed = [...now.keys()].filter((k) => !then.has(k)).length;
  return { added, changed, removed, unchanged };
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** "21 files: 3 new, 1 changed, 2 removed, 15 unchanged." (only the parts that are not 0). */
export function geosoftTemplateDiffWords(d: GeosoftTemplateDiff): string {
  const total = d.added + d.changed + d.unchanged;
  const bits = [
    d.added ? `${d.added} new` : '',
    d.changed ? `${d.changed} changed` : '',
    d.removed ? `${d.removed} removed` : '',
    d.unchanged ? `${d.unchanged} unchanged` : '',
  ].filter(Boolean);
  return `${plural(total, 'file')}: ${bits.join(', ')}.`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dayWords(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** The newest change of a set: when and by whom. */
function newest(rows: readonly GeosoftTemplateFile[]): GeosoftTemplateFile | undefined {
  return [...rows].sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
}

/** The live rows of one type. */
export function geosoftTemplateSet(
  rows: readonly GeosoftTemplateFile[],
  family: QcFamily,
): GeosoftTemplateFile[] {
  return rows
    .filter((r) => r.family === family && r.deleted_at === null)
    .sort((a, b) => a.rel_path.localeCompare(b.rel_path));
}

/** "21 files (148 KB), uploaded 30 Oct 2026 by Michael Brayne." or "No template in Teslyn Cloud." */
export function geosoftTemplateSetWords(rows: readonly GeosoftTemplateFile[], family: QcFamily): string {
  const set = geosoftTemplateSet(rows, family);
  if (set.length === 0) return 'No template in Teslyn Cloud.';
  const bytes = set.reduce((s, r) => s + r.size_bytes, 0);
  const size =
    bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / 1048576).toFixed(1)} MB`;
  const last = newest(set)!;
  const by = last.uploaded_by_name ? ` by ${last.uploaded_by_name}` : '';
  return `${plural(set.length, 'file')} (${size}), uploaded ${dayWords(last.updated_at)}${by}.`;
}

/** A type's set as one laptop holds it (Teslyn Field `geosoft_template_get`, `cloud`). */
export interface GeosoftTemplateCloudSet {
  readonly family: QcFamily;
  /** Live files in the manifest. */
  readonly files: number;
  /** Of those, the ones on this laptop with their hash. */
  readonly ready: number;
  readonly updated_at: string | null;
  readonly uploaded_by_name: string;
}

/** The Field panel's words for a type's Teslyn Cloud set on this laptop. */
export function geosoftTemplateCloudWords(
  set: GeosoftTemplateCloudSet | undefined,
  family: QcFamily,
): string {
  const label = AIRFRAME_LABEL[family];
  if (!set || set.files === 0) return `${label}: no template in Teslyn Cloud.`;
  const when = set.updated_at
    ? ` Uploaded ${dayWords(set.updated_at)}${set.uploaded_by_name ? ` by ${set.uploaded_by_name}` : ''}.`
    : '';
  if (set.ready >= set.files)
    return `${label}: ${plural(set.files, 'file')} from Teslyn Cloud, on this laptop.${when}`;
  return `${label}: downloading from Teslyn Cloud, ${set.ready} of ${plural(set.files, 'file')} on this laptop (each sync downloads the rest).${when}`;
}
